import type { Moment } from 'moment';
import { store } from '../state/store';
import type { WSResponse } from '../models/WSResponse';
import type { DepartmentResponse } from '../models/Department';
import type {
  ReportDepartment,
  DepartmentMemberTitle,
  DailyTimeReportPerson,
  TimeLogSave
} from '../models/DailyTimeReport';
import type { AuthUser } from './auth/constants';
import { SecurityListIds } from '../models/SecurityList';
import PersonService from './person/PersonService';
import UtilityService from './UtilityService';
import {
  setUserDepartmentId, setAllDepartments, setDepartmentMembers,
  setFetchingData, setReportPersons, setReportNote, setBuildingReport
} from '../state/slices/DailyTimeReportReducer';

const { dispatch } = store;

interface IDTRService {
  loadReportData: () => Promise<void>
  researchDepartmentMembers: (departmentId: string) => void
  getDepartmentMembers: (departmentId: string, startOfDay: number, endOfDay: number) => Promise<void>
  getDailyLogs: (members: DepartmentMemberTitle[], startOfDay: number, endOfDay: number) => Promise<void>
  getNote: (departmentId: string, startOfDay: number, endOfDay: number) => Promise<void>
  updateNote: (departmentId: string, newNote: string) => Promise<void>
  saveDailyTimeLogs: (props: TimeLogSave) => Promise<void>
  startSubmitDailyTimes: (departmentId: string) => Promise<void>
}

/**
 * Daily Time Report Service handles getting any data from backend for daily Time Report
 */
class DTRService implements IDTRService {
  async loadReportData (): Promise<void> {
    PersonService.fetchUserDepartmentId()
      .then((resp: WSResponse) => {
        if (UtilityService.isSuccessResponse(resp.responseCode) && resp.responseMessage) {
          const departmentId: string = resp.responseMessage;
          const authState = store.getState().auth;
          const crooglooAuth: AuthUser = authState.crooglooauth;
          const departments: DepartmentResponse[] = store.getState().person.departments;
          dispatch(setUserDepartmentId({ departmentId }));
          const isAdmin: boolean = crooglooAuth.securityListId?.toLowerCase() === SecurityListIds.ADMIN.toLowerCase();
          let departmentsForReport: ReportDepartment[] = departments.map((department: DepartmentResponse) => ({
            id: department.properties.id,
            name: department.properties.departmentName
          }));
          if (!isAdmin) {
            departmentsForReport = departmentsForReport.filter((department: ReportDepartment) => department.id === departmentId);
          }
          dispatch(setAllDepartments({ departments: departmentsForReport }));
          const selectedDate: Moment = store.getState().dailyTimeReport.selectedDate;
          const startOfDay: number = selectedDate.startOf('day').valueOf();
          const endOfDay: number = selectedDate.endOf('day').valueOf();
          void this.getNote(departmentId, startOfDay, endOfDay)
          void this.getDepartmentMembers(departmentId, startOfDay, endOfDay);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  researchDepartmentMembers (departmentId: string): void {
    dispatch(setFetchingData({ fetching: true }));
    const selectedDate: Moment = store.getState().dailyTimeReport.selectedDate;
    const startOfDay: number = selectedDate.startOf('day').valueOf();
    const endOfDay: number = selectedDate.endOf('day').valueOf();
    void this.getNote(departmentId, startOfDay, endOfDay);
    void this.getDepartmentMembers(departmentId, startOfDay, endOfDay);
  }

  async getDepartmentMembers (departmentId: string, startOfDay: number, endOfDay: number): Promise<void> {
    PersonService.fetchDepartmentMembersTitle(departmentId, startOfDay, endOfDay)
      .then((members: DepartmentMemberTitle[]) => {
        dispatch(setDepartmentMembers({ members }));
        void this.getDailyLogs(members, startOfDay, endOfDay);
      })
      .catch((err) => {
        console.error(err);
      })
  }

  async getDailyLogs (members: DepartmentMemberTitle[], startOfDay: number, endOfDay: number): Promise<void> {
    PersonService.fetchDailyTimeLogs(members, startOfDay, endOfDay)
      .then((reportPersons: DailyTimeReportPerson[]) => {
        dispatch(setReportPersons({ members: reportPersons }));
        dispatch(setFetchingData({ fetching: false }));
      })
      .catch((err) => {
        console.error(err);
      })
  }

  async getNote (departmentId: string, startOfDay: number, endOfDay: number): Promise<void> {
    PersonService.fetchDailyTimeReportNote(departmentId, startOfDay, endOfDay)
      .then((resp: WSResponse) => {
        if (UtilityService.isSuccessResponse(resp.responseCode) && resp.responseMessage) {
          dispatch(setReportNote({ note: resp.responseMessage }));
        }
      })
      .catch((err) => {
        console.error(err);
      })
  }

  async updateNote (departmentId: string, newNote: string): Promise<void> {
    const selectedDate: Moment = store.getState().dailyTimeReport.selectedDate;
    const startOfDay: number = selectedDate.startOf('day').valueOf();
    const endOfDay: number = selectedDate.endOf('day').valueOf();
    PersonService.updateDailyTimeReportNote(departmentId, startOfDay, endOfDay, newNote, selectedDate.valueOf())
      .then((resp: WSResponse) => {
        if (UtilityService.isSuccessResponse(resp.responseCode)) {
          console.debug('successfully update department note');
        } else {
          throw new Error('invalid server response');
        }
      })
      .catch((err) => {
        console.error(err);
      })
  }

  async saveDailyTimeLogs (props: TimeLogSave): Promise<void> {
    const selectedDate: Moment = store.getState().dailyTimeReport.selectedDate;
    const targetTime: number = selectedDate.valueOf();
    if (props.call_type === 'not-in') {
      props.travel_start_time = '-1';
      props.call_time = '-1';
      props.first_meal_start_time = '-1';
      props.first_meal_end_time = '-1';
      props.second_meal_start_time = '-1';
      props.second_meal_end_time = '-1';
      props.wrap_time = '-1';
      props.travel_end_time = '-1';
      props.kit_time = '';
      props.upgrade_time = '';
    }
    props.targetReportTime = targetTime;
    console.log(props);
    PersonService.saveDailyTimeLogs(props)
      .then((resp: WSResponse) => {
        if (UtilityService.isSuccessResponse(resp.responseCode)) {
          console.debug('successfully updated daily/weekly call status');
        } else {
          throw new Error('invalid server response');
        }
      })
      .catch((err) => {
        console.error(err);
      })
  }

  async startSubmitDailyTimes (departmentId: string): Promise<void> {
    dispatch(setBuildingReport({ buildingReport: true }));
    const selectedDate: Moment = store.getState().dailyTimeReport.selectedDate;
    const startOfDay: number = selectedDate.startOf('day').valueOf();
    const endOfDay: number = selectedDate.endOf('day').valueOf();
    await PersonService.fetchDepartmentMembersTitle(departmentId, startOfDay, endOfDay)
      .then(async (members: DepartmentMemberTitle[]) => {
        if (members.length > 0) {
          dispatch(setDepartmentMembers({ members }));
          await PersonService.fetchDailyTimeLogs(members, startOfDay, endOfDay, true)
            .then((reportPersons: DailyTimeReportPerson[]) => {
              dispatch(setReportPersons({ members: reportPersons }));
            })
            .catch((err) => {
              throw new Error(err.message);
            });
        } else {
          throw new Error('no members gotten');
        }
      })
      .catch((err) => {
        console.error(err);
        throw new Error('problem getting data');
      })
  }
}

const dtrService: IDTRService = new DTRService();
export default dtrService;
